import { useMemo, useRef, useState, type DragEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import BackupIcon from '@mui/icons-material/BackupOutlined';
import { useData } from '../store/data';
import { useConfig } from '../store/config';
import { useFilters } from '../store/filters';
import { state as stateApi } from '../api/client';
import { downloadBackup, parseBackupFile, type Backup } from '../lib/backup';
import { fmtInt, formatBytes, formatDateTime } from '../lib/format';
import { useT, format } from '../i18n';

type Props = { open: boolean; onClose: () => void };

type RestoreMode = 'replace' | 'merge';

// Modal mit zwei Sektionen:
//   • Sichern  → JSON-Snapshot herunterladen
//   • Wiederherstellen → JSON-Datei einlesen, Optionen wählen, Backend
//     atomar überschreiben.
export function BackupDialog({ open, onClose }: Props) {
  // Daten zum Backup-Bauen
  const rows           = useData(s => s.rows);
  const meta           = useData(s => s.meta);
  const loadData       = useData(s => s.loadFromBackend);
  const releaseCal     = useConfig(s => s.releaseCal);
  const capacityByTeam = useConfig(s => s.capacityByTeam);
  const initialPlan    = useConfig(s => s.initialPlan);
  const loadConfig     = useConfig(s => s.loadFromBackend);
  const spToPtFactor   = useFilters(s => s.spToPtFactor);

  // Restore-State
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<Backup | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [mode, setMode] = useState<RestoreMode>('replace');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const t = useT();

  const stats = useMemo(() => ({
    rows:           rows.length,
    releases:       Object.keys(releaseCal).length,
    capacityTeams:  Object.keys(capacityByTeam).length,
    initialPlans:   Object.keys(initialPlan).length,
  }), [rows, releaseCal, capacityByTeam, initialPlan]);

  function doDownload() {
    const backup: Backup = {
      version: 'v2',
      exportedAt: new Date().toISOString(),
      meta,
      rows,
      releaseCal,
      capacityByTeam,
      initialPlan,
      spToPtFactor,
    };
    downloadBackup(backup);
  }

  async function onPickFile(files: FileList | File[] | null) {
    setError(null);
    setResult(null);
    setParsed(null);
    setWarnings([]);
    if (!files || !files.length) return;
    const f = Array.from(files)[0];
    if (!f.name.toLowerCase().endsWith('.json')) {
      setError('.json');
      return;
    }
    setFile(f);
    try {
      const r = await parseBackupFile(f);
      setParsed(r.backup);
      setWarnings(r.warnings);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setFile(null);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    void onPickFile(e.dataTransfer.files);
  }

  async function doRestore() {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      // Beim Replace: rows komplett ersetzen, config komplett überschreiben
      // Beim Merge:   rows mergen (dedup nach Issue key), config tiefer mergen.
      const finalRows = mode === 'replace'
        ? parsed.rows
        : mergeRows(rows, parsed.rows);
      const finalCfg = mode === 'replace'
        ? {
            meta:           parsed.meta,
            releaseCal:     parsed.releaseCal,
            capacityByTeam: parsed.capacityByTeam,
            initialPlan:    parsed.initialPlan,
            spToPtFactor:   parsed.spToPtFactor,
          }
        : {
            meta:           parsed.meta ?? meta,
            releaseCal:     { ...releaseCal, ...parsed.releaseCal },
            capacityByTeam: mergeCapacity(capacityByTeam, parsed.capacityByTeam),
            initialPlan:    { ...initialPlan, ...parsed.initialPlan },
            spToPtFactor:   parsed.spToPtFactor || spToPtFactor,
          };

      await stateApi.saveRows(finalRows);
      await stateApi.saveConfig(finalCfg);

      // Stores neu aus dem Backend laden, damit alle Komponenten sich
      // konsistent updaten
      await loadData();
      await loadConfig();

      setResult(format(t.backup.restoreOk, {
        issues: fmtInt(finalRows.length),
        releases: Object.keys(finalCfg.releaseCal ?? {}).length,
      }));
      setFile(null);
      setParsed(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function close() {
    if (busy) return;
    onClose();
    setTimeout(() => {
      setFile(null); setParsed(null); setWarnings([]);
      setError(null); setResult(null); setMode('replace');
    }, 200);
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BackupIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{t.backup.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t.backup.subtitle}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* ── Sichern ─────────────────────────────────────────── */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">{t.backup.exportCardTitle}</Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {t.backup.exportCardBody}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Chip size="small" label={format(t.backup.countIssues, { count: fmtInt(stats.rows) })} />
                <Chip size="small" label={format(t.backup.countReleases, { count: stats.releases })} variant="outlined" />
                <Chip size="small" label={format(t.backup.countTeamsCapa, { count: stats.capacityTeams })} variant="outlined" />
                <Chip size="small" label={format(t.backup.countInitialPlans, { count: stats.initialPlans })} variant="outlined" />
              </Stack>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={doDownload}>
                {t.backup.download}
              </Button>
            </CardContent>
          </Card>

          {/* ── Wiederherstellen ────────────────────────────────── */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">{t.backup.restoreCardTitle}</Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {t.backup.restoreCardBody}
              </Typography>

              <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  bgcolor: dragOver ? 'action.hover' : 'transparent',
                  borderRadius: 3,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 120ms ease',
                  mb: 1.5,
                }}
              >
                <UploadFileIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="body2">{t.backup.dropzone}</Typography>
                <Typography variant="caption" color="text.secondary">{t.backup.dropzoneHint}</Typography>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={(e) => onPickFile(e.target.files)}
                />
              </Box>

              {file && parsed && (
                <Card variant="outlined" sx={{ mb: 1.5, borderColor: 'primary.main' }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(t.backup.fileMeta, { size: formatBytes(file.size), date: formatDateTime(parsed.exportedAt) })}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={format(t.backup.countIssues, { count: fmtInt(parsed.rows.length) })} color="primary" />
                      <Chip size="small" label={format(t.backup.countReleases, { count: Object.keys(parsed.releaseCal).length })} variant="outlined" />
                      <Chip size="small" label={format(t.backup.countTeamsCapa, { count: Object.keys(parsed.capacityByTeam).length })} variant="outlined" />
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {warnings.map(w => (
                <Alert key={w} severity="warning" variant="outlined" sx={{ mb: 1 }}>{w}</Alert>
              ))}

              {parsed && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>{t.backup.modeQuestion}</Typography>
                  <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as RestoreMode)}>
                    <FormControlLabel value="replace" control={<Radio />} label={
                      <Box>
                        <Typography variant="body2"><b>{t.backup.modeReplace}</b></Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.backup.modeReplaceHint}
                        </Typography>
                      </Box>
                    } />
                    <FormControlLabel value="merge" control={<Radio />} label={
                      <Box>
                        <Typography variant="body2"><b>{t.backup.modeMerge}</b></Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.backup.modeMergeHint}
                        </Typography>
                      </Box>
                    } />
                  </RadioGroup>
                </Box>
              )}

              {busy && <LinearProgress sx={{ mt: 1 }} />}
              {error  && <Alert severity="error"   sx={{ mt: 1 }}>{error}</Alert>}
              {result && <Alert severity="success" sx={{ mt: 1 }}>{result}</Alert>}
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={close} disabled={busy}>{t.backup.close}</Button>
        <Button
          variant="contained"
          onClick={doRestore}
          disabled={busy || !parsed}
          color={mode === 'replace' ? 'warning' : 'primary'}
        >
          {busy ? t.backup.restoring : (mode === 'replace' ? t.backup.replace : t.backup.merge)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Merge-Helpers ────────────────────────────────────────────────────

function mergeRows(current: Backup['rows'], incoming: Backup['rows']): Backup['rows'] {
  const map = new Map<string, Backup['rows'][number]>();
  for (const r of current)  if (typeof r['Issue key'] === 'string') map.set(r['Issue key'], r);
  for (const r of incoming) if (typeof r['Issue key'] === 'string') map.set(r['Issue key'], r);
  return Array.from(map.values());
}

function mergeCapacity(a: Backup['capacityByTeam'], b: Backup['capacityByTeam']): Backup['capacityByTeam'] {
  const out: Backup['capacityByTeam'] = { ...a };
  for (const [team, rels] of Object.entries(b)) {
    out[team] = { ...(out[team] ?? {}) };
    for (const [rel, months] of Object.entries(rels)) {
      out[team][rel] = { ...(out[team][rel] ?? {}), ...months };
    }
  }
  return out;
}
